// Sprint 2026-Feb-12 — Estimate Pending Ageing Summary (Client POC Wise)
//
// Path: /reports/estimate-ageing
// Endpoints:
//   POST /api/reports/estimate-ageing/summary  → grouped-by-POC summary + card totals
//   POST /api/reports/estimate-ageing/drill    → estimate list for one POC (+ optional bucket)
//   POST /api/reports/estimate-ageing/export.{xlsx|pdf|csv}
//
// Layout:
//   ┌────────────────────────────────────────────────────────┐
//   │ PageHeader                          [Export XLSX/PDF/… │
//   ├────────────────────────────────────────────────────────┤
//   │ Filter card (collapsible)                              │
//   ├────────────────────────────────────────────────────────┤
//   │ 7 dashboard cards — Total + 6 ageing buckets (clickable│
//   ├────────────────────────────────────────────────────────┤
//   │ Grouped table — POC rows, bucket cells drill on click  │
//   ├────────────────────────────────────────────────────────┤
//   │ Grand-totals row                                       │
//   └────────────────────────────────────────────────────────┘
//   + Drill Modal (list of estimates for one POC / bucket)
//     Row actions: Preview · Send Email · Open Estimate
//
// Uses the existing PreviewDialog + ManualSend flow from the
// EstimateEmailPanel component (Sprint 11i) — nothing new to build.

import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-helpers";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Download, FileText, FileSpreadsheet, Printer, Filter, RotateCw,
  X, Send, Eye, ExternalLink, ClipboardList,
} from "lucide-react";

// Bucket keys must match backend `BUCKET_KEYS` (spec-locked).
const BUCKETS = [
  { key: "b_0_3",     label: "0 – 3 Days",       short: "0-3",   color: "emerald" },
  { key: "b_3_7",     label: "Above 3 – 7 Days", short: "3-7",   color: "sky" },
  { key: "b_7_15",    label: "7 – 15 Days",      short: "7-15",  color: "amber" },
  { key: "b_15_30",   label: "15 – 30 Days",     short: "15-30", color: "orange" },
  { key: "b_30_45",   label: "30 – 45 Days",     short: "30-45", color: "rose" },
  { key: "b_45_plus", label: "Above 45 Days",    short: ">45",   color: "red" },
];

const CARD_TONE = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  sky:     "border-sky-200 bg-sky-50 text-sky-900",
  amber:   "border-amber-200 bg-amber-50 text-amber-900",
  orange:  "border-orange-200 bg-orange-50 text-orange-900",
  rose:    "border-rose-200 bg-rose-50 text-rose-900",
  red:     "border-red-300 bg-red-100 text-red-900",
  slate:   "border-slate-300 bg-slate-100 text-slate-900",
};

const _EMPTY_FILTERS = {
  fy_code: "", from_date: "", to_date: "",
  branch_id: "", project_id: "", client_id: "",
  client_poc: "", salesman: "", status: "",
  min_pending_days: "", max_pending_days: "",
  bucket: "",
};

function fmtMoney(v) {
  return `₹ ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}
function fmtDate(v) {
  if (!v) return "—";
  const s = String(v).slice(0, 10);
  try {
    const d = new Date(s);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return s; }
}

export default function EstimateAgeingReport() {
  const [filters, setFilters] = useState({ ..._EMPTY_FILTERS });
  const [data, setData] = useState({ rows: [], card_totals: null, grand_totals: null });
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [lookup, setLookup] = useState({ branches: [], projects: [], clients: [] });
  const [drill, setDrill] = useState(null);   // {poc_key, poc_name, client_name, bucket}
  const [exporting, setExporting] = useState("");
  // Jul-2026 — POC dimension views:
  //   primary   → group by estimate.agent (existing behaviour, default)
  //   secondary → group by estimate.agent_secondary
  //   hierarchy → Client → Secondary POC pivot (frontend grouping of the
  //               secondary dataset)
  const [view, setView] = useState("primary");

  useEffect(() => {
    // Lookups run once — matches ReportsV2.jsx pattern.
    Promise.all([
      api.get("/branches").catch(() => ({ data: [] })),
      api.get("/projects-picker").catch(() => ({ data: [] })),
      api.get("/clients/picker").catch(() => ({ data: [] })),
    ]).then(([b, p, c]) => setLookup({
      branches: b.data || [], projects: p.data || [], clients: c.data || [],
    })).catch(() => {});
    // Auto-run once on mount so users see numbers immediately.
    run(_EMPTY_FILTERS);
  }, []);

  function setField(k, v) { setFilters(f => ({ ...f, [k]: v })); }

  async function run(overrides, v) {
    setLoading(true);
    try {
      const body = normalizeFilters(overrides || filters, v);
      const { data: d } = await api.post("/reports/estimate-ageing/summary", body);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report failed");
    } finally { setLoading(false); }
  }

  function normalizeFilters(f, v) {
    const b = { ...f, group_by: ((v || view) === "primary" ? "primary" : "secondary") };
    for (const k of ["min_pending_days", "max_pending_days"]) {
      b[k] = b[k] === "" || b[k] === null ? null : Number(b[k]);
    }
    return b;
  }

  function reset() {
    setFilters({ ..._EMPTY_FILTERS });
    run(_EMPTY_FILTERS);
  }

  function applyBucket(bucket) {
    const next = { ...filters, bucket: filters.bucket === bucket ? "" : bucket };
    setFilters(next); run(next);
  }

  async function doExport(kind) {
    setExporting(kind);
    try {
      const res = await api.post(
        `/reports/estimate-ageing/export.${kind}`,
        normalizeFilters(filters),
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estimate_ageing_summary.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${kind.toUpperCase()} export failed`);
    } finally { setExporting(""); }
  }

  function printReport() {
    window.print();
  }

  const rows      = data.rows || [];
  const cardTotals = data.card_totals || {};
  const grand     = data.grand_totals || {};

  // Client → Secondary POC pivot groups (hierarchy view only).
  const hierarchyGroups = (() => {
    if (view !== "hierarchy") return [];
    const map = new Map();
    for (const r of rows) {
      const k = r.client_id || "";
      if (!map.has(k)) {
        map.set(k, { client_id: k, client_name: r.client_name || "", client_code: r.client_code || "",
                     rows: [], totals: { total_estimates: 0, total_amount: 0 } });
      }
      const g = map.get(k);
      g.rows.push(r);
      for (const b of BUCKETS) g.totals[b.key] = (g.totals[b.key] || 0) + (r[b.key] || 0);
      g.totals.total_estimates += r.total_estimates || 0;
      g.totals.total_amount += r.total_amount || 0;
    }
    return [...map.values()].sort((a, b) => (a.client_name || "").localeCompare(b.client_name || ""));
  })();

  const pocColLabel = view === "primary" ? "POC Name" : "Secondary POC";

  function pocRow(r, indent = false) {
    return (
      <tr key={`${r.client_id}::${r.poc_name}`}
          className="border-b border-slate-100 hover:bg-orange-50/40"
          data-testid={`row-${r.client_id}-${r.poc_name}`}>
        <Td>
          {indent ? (
            <span className="text-slate-300 pl-4">↳</span>
          ) : (
            <>
              <div className="font-medium text-slate-800">{r.client_name || "—"}</div>
              {r.client_code ? (
                <div className="text-[9px] text-slate-500 font-mono">{r.client_code}</div>
              ) : null}
            </>
          )}
        </Td>
        <Td>{r.poc_name || <span className="italic text-slate-400">— (not set)</span>}</Td>
        <Td className="font-mono text-[10px] break-all">{r.poc_email || "—"}</Td>
        <Td className="font-mono text-[10px]">{r.poc_mobile || "—"}</Td>
        {BUCKETS.map(b => (
          <Td key={b.key} className="text-center p-0">
            <button
              className={`w-full h-full px-2 py-2 tabular-nums ${
                r[b.key] > 0
                  ? `hover:bg-${b.color}-100 font-bold text-${b.color}-800 cursor-pointer`
                  : "text-slate-300"
              }`}
              disabled={r[b.key] === 0}
              onClick={() => setDrill({
                poc_key: `${r.client_id}::${r.poc_name || ""}`,
                poc_name: r.poc_name || "",
                client_name: r.client_name || "",
                bucket: b.key,
              })}
              data-testid={`cell-${r.client_id}-${r.poc_name}-${b.key}`}>
              {r[b.key] || "—"}
            </button>
          </Td>
        ))}
        <Td className="text-right font-bold text-slate-800">
          <button
            className="hover:underline"
            onClick={() => setDrill({
              poc_key: `${r.client_id}::${r.poc_name || ""}`,
              poc_name: r.poc_name || "",
              client_name: r.client_name || "",
              bucket: "",
            })}
            data-testid={`total-${r.client_id}-${r.poc_name}`}>
            {r.total_estimates}
          </button>
        </Td>
        <Td className="text-right tabular-nums">{fmtMoney(r.total_amount)}</Td>
        <Td className="whitespace-nowrap">{fmtDate(r.last_estimate_date)}</Td>
        <Td className="whitespace-nowrap">{fmtDate(r.oldest_pending_date)}</Td>
        <Td className="text-right tabular-nums">
          {Math.round(r.avg_pending_days ?? 0)}
        </Td>
      </tr>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="estimate-ageing-report">
      <PageHeader
        eyebrow="Reports & Registers"
        title="Estimate Pending Ageing Summary"
        subtitle={view === "primary" ? "Client POC Wise · follow-up management dashboard"
          : view === "secondary" ? "Secondary Client POC Wise · store-level follow-up"
          : "Client → Secondary POC hierarchy · pending estimates pivot"}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowFilters(v => !v)}
                    data-testid="btn-toggle-filters">
              <Filter className="w-4 h-4 mr-1" /> {showFilters ? "Hide" : "Show"} Filters
            </Button>
            <Button variant="outline" size="sm" onClick={() => run(filters)} disabled={loading}
                    data-testid="btn-refresh">
              <RotateCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Run
            </Button>
            <Button variant="outline" size="sm"
                    onClick={() => doExport("xlsx")} disabled={!!exporting}
                    data-testid="btn-export-xlsx">
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              {exporting === "xlsx" ? "Exporting…" : "Excel"}
            </Button>
            <Button variant="outline" size="sm"
                    onClick={() => doExport("pdf")} disabled={!!exporting}
                    data-testid="btn-export-pdf">
              <FileText className="w-4 h-4 mr-1" />
              {exporting === "pdf" ? "Exporting…" : "PDF"}
            </Button>
            <Button variant="outline" size="sm"
                    onClick={() => doExport("csv")} disabled={!!exporting}
                    data-testid="btn-export-csv">
              <Download className="w-4 h-4 mr-1" />
              {exporting === "csv" ? "Exporting…" : "CSV"}
            </Button>
            <Button variant="outline" size="sm" onClick={printReport}
                    data-testid="btn-print">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
          </div>
        }
      />

      {/* ── Filter card ─────────────────────────────────────── */}
      {showFilters && (
        <Card data-testid="filters-card">
          <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <Field label="Estimate Date From">
              <Input type="date" value={filters.from_date}
                     onChange={e => setField("from_date", e.target.value)}
                     data-testid="fltr-from-date" />
            </Field>
            <Field label="Estimate Date To">
              <Input type="date" value={filters.to_date}
                     onChange={e => setField("to_date", e.target.value)}
                     data-testid="fltr-to-date" />
            </Field>
            <Field label="Branch">
              <Select value={filters.branch_id}
                      onChange={v => setField("branch_id", v)}
                      options={lookup.branches.map(b => ({
                        value: b.id, label: b.branch_name || b.name || b.id,
                      }))}
                      placeholder="All Branches" testid="fltr-branch" />
            </Field>
            <Field label="Project">
              <Select value={filters.project_id}
                      onChange={v => setField("project_id", v)}
                      options={lookup.projects.map(p => ({
                        value: p.id, label: p.name || p.project_name || p.id,
                      }))}
                      placeholder="All Projects" testid="fltr-project" />
            </Field>
            <Field label="Client">
              <Select value={filters.client_id}
                      onChange={v => setField("client_id", v)}
                      options={lookup.clients.map(c => ({
                        value: c.id, label: c.name || c.id,
                      }))}
                      placeholder="All Clients" testid="fltr-client" />
            </Field>
            <Field label="Client POC">
              <Input value={filters.client_poc}
                     onChange={e => setField("client_poc", e.target.value)}
                     placeholder="POC name" data-testid="fltr-poc" />
            </Field>
            <Field label="Salesman">
              <Input value={filters.salesman}
                     onChange={e => setField("salesman", e.target.value)}
                     placeholder="Sales executive" data-testid="fltr-salesman" />
            </Field>
            <Field label="Status">
              <Input value={filters.status}
                     onChange={e => setField("status", e.target.value)}
                     placeholder="Any (excl. Approved/Cancelled/…)"
                     data-testid="fltr-status" />
            </Field>
            <Field label="Pending Days ≥">
              <Input type="number" value={filters.min_pending_days}
                     onChange={e => setField("min_pending_days", e.target.value)}
                     placeholder="min" data-testid="fltr-min-days" />
            </Field>
            <Field label="Pending Days ≤">
              <Input type="number" value={filters.max_pending_days}
                     onChange={e => setField("max_pending_days", e.target.value)}
                     placeholder="max" data-testid="fltr-max-days" />
            </Field>
            <div className="col-span-2 md:col-span-4 lg:col-span-5 flex items-center gap-2 pt-1">
              <Button size="sm" onClick={() => run(filters)} disabled={loading}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      data-testid="btn-apply-filters">
                Apply
              </Button>
              <Button size="sm" variant="outline" onClick={reset}
                      data-testid="btn-reset-filters">
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
              {filters.bucket && (
                <div className="ml-auto text-[11px] text-slate-600 flex items-center gap-1"
                     data-testid="bucket-chip">
                  Bucket filter:
                  <span className="px-1.5 py-0.5 bg-slate-800 text-white uppercase tracking-wider">
                    {BUCKETS.find(b => b.key === filters.bucket)?.label}
                  </span>
                  <button className="text-slate-500 hover:text-slate-800"
                          onClick={() => applyBucket(filters.bucket)}>×</button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── View toggle (Jul-2026) ────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-0 border border-slate-300 w-fit overflow-hidden" data-testid="ageing-view-toggle">
        {[["primary", "Primary POC"], ["secondary", "Secondary POC"], ["hierarchy", "Client → Secondary POC"]].map(([k, label]) => (
          <button key={k} type="button" data-testid={`ageing-view-${k}`}
            onClick={() => { setView(k); run(filters, k); }}
            className={`px-4 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
              view === k ? "bg-[#0B1F3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Dashboard cards ───────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3"
           data-testid="dashboard-cards">
        <DashboardCard
          testid="card-total"
          label="Total Pending"
          tone="slate"
          count={cardTotals.total_pending_estimates ?? 0}
          sub={fmtMoney(cardTotals.total_pending_amount ?? 0)}
          onClick={() => applyBucket("")}
          active={!filters.bucket}
        />
        {BUCKETS.map(b => (
          <DashboardCard
            key={b.key}
            testid={`card-${b.key}`}
            label={b.label}
            tone={b.color}
            count={cardTotals[b.key] ?? 0}
            onClick={() => applyBucket(b.key)}
            active={filters.bucket === b.key}
          />
        ))}
      </div>

      {/* ── Grouped POC table ─────────────────────────────────── */}
      <Card data-testid="poc-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[65vh]">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr className="border-b border-slate-300">
                  <Th>Client</Th>
                  <Th>{pocColLabel}</Th>
                  <Th>Email</Th>
                  <Th>Mobile</Th>
                  {BUCKETS.map(b => (
                    <Th key={b.key} className="text-center whitespace-nowrap">
                      {b.short}
                    </Th>
                  ))}
                  <Th className="text-right">Total</Th>
                  <Th className="text-right">Amount</Th>
                  <Th className="whitespace-nowrap">Last Est.</Th>
                  <Th className="whitespace-nowrap">Oldest</Th>
                  <Th className="text-right whitespace-nowrap">Avg Days</Th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={14} className="p-10 text-center text-slate-400">
                    <RotateCw className="w-5 h-5 mx-auto animate-spin" />
                  </td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={14} className="p-10 text-center text-slate-400 italic"
                          data-testid="empty-state">
                    No pending estimates match the current filters.
                  </td></tr>
                )}
                {!loading && view !== "hierarchy" && rows.map((r) => pocRow(r))}
                {!loading && view === "hierarchy" && hierarchyGroups.map((g) => (
                  <React.Fragment key={g.client_id}>
                    <tr className="border-b border-slate-200 bg-slate-50" data-testid={`client-group-${g.client_id}`}>
                      <Td colSpan={4}>
                        <span className="font-black text-slate-800 text-[12px]">{g.client_name || "—"}</span>
                        {g.client_code && <span className="ml-2 text-[9px] text-slate-500 font-mono">{g.client_code}</span>}
                        <span className="ml-2 text-[10px] text-slate-400">({g.rows.length} POC{g.rows.length !== 1 ? "s" : ""})</span>
                      </Td>
                      {BUCKETS.map(b => (
                        <Td key={b.key} className="text-center tabular-nums font-bold text-slate-700">{g.totals[b.key] || "—"}</Td>
                      ))}
                      <Td className="text-right font-black text-slate-800">{g.totals.total_estimates}</Td>
                      <Td className="text-right tabular-nums font-bold">{fmtMoney(g.totals.total_amount)}</Td>
                      <Td colSpan={3}></Td>
                    </tr>
                    {g.rows.map((r) => pocRow(r, true))}
                  </React.Fragment>
                ))}
              </tbody>
              {/* Grand totals row */}
              {!loading && rows.length > 0 && (
                <tfoot className="bg-slate-100 border-t-2 border-slate-400 font-bold sticky bottom-0">
                  <tr data-testid="grand-totals-row">
                    <Td colSpan={4} className="text-right">GRAND TOTAL</Td>
                    {BUCKETS.map(b => (
                      <Td key={b.key} className="text-center tabular-nums">
                        {grand[b.key] || 0}
                      </Td>
                    ))}
                    <Td className="text-right tabular-nums" data-testid="grand-total-estimates">
                      {grand.total_estimates || 0}
                    </Td>
                    <Td className="text-right tabular-nums" data-testid="grand-total-amount">
                      {fmtMoney(grand.total_amount || 0)}
                    </Td>
                    <Td colSpan={3}></Td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      {drill && (
        <DrillModal
          drill={drill}
          filters={normalizeFilters(filters)}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Select({ value, onChange, options, placeholder, testid }) {
  return (
    <select value={value || ""} onChange={e => onChange(e.target.value)}
            data-testid={testid}
            className="w-full border rounded px-2 py-1.5 text-xs bg-white">
      <option value="">{placeholder || "All"}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-3 py-2 text-left text-[9px] uppercase tracking-wider font-bold text-slate-600 ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = "", colSpan, ...rest }) {
  return <td colSpan={colSpan} className={`px-3 py-2 align-top ${className}`} {...rest}>{children}</td>;
}

function DashboardCard({ label, count, sub, tone, onClick, active, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left border px-3 py-3 hover:shadow-md transition-all ${CARD_TONE[tone]} ${
        active ? "ring-2 ring-orange-500 ring-offset-1" : ""
      }`}
    >
      <div className="text-[9px] uppercase tracking-wider font-bold opacity-70">{label}</div>
      <div className="text-2xl font-black tabular-nums mt-1">{count.toLocaleString("en-IN")}</div>
      {sub && <div className="text-[10px] mt-1 opacity-80">{sub}</div>}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Drill Modal — list of estimates for one POC / bucket combination.
// Row actions: Preview · Send Email · Open Estimate.
// Preview + Send Email reuse the Sprint-11i PreviewDialog + Manual
// Send flow via lazy import — no duplication.
// ─────────────────────────────────────────────────────────────────
function DrillModal({ drill, filters, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.post("/reports/estimate-ageing/drill", {
      ...filters,
      poc_key: drill.poc_key,
      bucket: drill.bucket || "",
    })
    .then(({ data }) => { if (!cancelled) setRows(data.rows || []); })
    .catch(e => toast.error(e?.response?.data?.detail || "Drill failed"))
    .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [drill.poc_key, drill.bucket]);

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="drill-modal">
      <div className="bg-white w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800 text-sm">
              Estimates for {drill.poc_name || "—"}
              {drill.bucket && (
                <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-slate-800 text-white px-1.5 py-0.5">
                  {BUCKETS.find(b => b.key === drill.bucket)?.label || drill.bucket}
                </span>
              )}
            </h3>
            <div className="text-[11px] text-slate-500">{drill.client_name}</div>
          </div>
          <button onClick={onClose}
                  className="text-slate-400 hover:text-slate-700 text-xl leading-none"
                  data-testid="drill-close">×</button>
        </div>

        <div className="flex-1 overflow-auto p-3">
          {loading && (
            <div className="p-6 text-center text-slate-400">
              <RotateCw className="w-5 h-5 mx-auto animate-spin" />
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="p-6 text-center text-slate-400 italic">No estimates match.</div>
          )}
          {!loading && rows.length > 0 && (
            <table className="w-full text-[11px]">
              <thead className="bg-slate-100 border-b border-slate-300">
                <tr>
                  <Th>Est #</Th>
                  <Th>Date</Th>
                  <Th>Project</Th>
                  <Th>Branch</Th>
                  <Th className="text-right">Amount</Th>
                  <Th className="text-right">Pending</Th>
                  <Th>Status</Th>
                  <Th>Last Email</Th>
                  <Th>Last Reminder</Th>
                  <Th>Salesman</Th>
                  <Th className="text-center">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <DrillRow key={r.id} r={r} />
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-between text-[11px]">
          <div className="text-slate-500">
            <ClipboardList className="w-3 h-3 inline mr-1" />
            {rows.length} estimate{rows.length === 1 ? "" : "s"}
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

function DrillRow({ r }) {
  async function preview() {
    // Uses the existing Estimate page's Email panel which already has
    // the full PreviewDialog (Sprint 11i). Open in a new tab so the
    // admin can preview + confirm without losing the drill context.
    window.open(`/estimates?open=${r.id}&panel=email&action=preview`,
                "_blank", "noopener");
  }

  async function sendEmail() {
    // Fire manual send via the existing endpoint — no override.
    try {
      const { data } = await api.post(`/estimates/${r.id}/send-email`, {});
      if (data.status === "sent") toast.success(`Email sent to ${data.recipient}`);
      else if (data.status === "failed") toast.error(`Send failed: ${data.failure_reason || "unknown"}`);
      else toast.warning(`Skipped: ${data.failure_reason || "?"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  }

  function openEstimate() {
    window.open(`/estimates?open=${r.id}`, "_blank", "noopener");
  }

  const emailBadge = ({
    sent:    "text-emerald-700 bg-emerald-50 border-emerald-200",
    failed:  "text-rose-700 bg-rose-50 border-rose-200",
    skipped: "text-slate-600 bg-slate-100 border-slate-200",
  })[r.last_email_status] || "text-slate-500 bg-slate-50 border-slate-200";

  return (
    <tr className="border-b border-slate-100 hover:bg-orange-50/30" data-testid={`drill-row-${r.id}`}>
      <Td className="font-mono">{r.estimate_number}</Td>
      <Td>{fmtDate(r.estimate_date)}</Td>
      <Td>{r.project_name || "—"}</Td>
      <Td>{r.branch_name || "—"}</Td>
      <Td className="text-right tabular-nums">{fmtMoney(r.total_amount)}</Td>
      <Td className={`text-right tabular-nums font-bold ${r.pending_days > 30 ? "text-rose-700" : ""}`}>
        {r.pending_days}
      </Td>
      <Td>
        <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-bold border bg-slate-50 border-slate-200">
          {r.status}
        </span>
      </Td>
      <Td className="whitespace-nowrap">
        {r.last_email_at ? (
          <div>
            <div>{fmtDate(r.last_email_at)}</div>
            <span className={`inline-block px-1 py-0 text-[9px] uppercase border ${emailBadge}`}>
              {r.last_email_status || "—"}
            </span>
          </div>
        ) : <span className="text-slate-400">Never</span>}
      </Td>
      <Td className="whitespace-nowrap">
        {r.last_reminder_at ? (
          <div>{fmtDate(r.last_reminder_at)}
            <div className="text-[9px] text-slate-500">
              {r.reminder_count || 0} sent
            </div>
          </div>
        ) : <span className="text-slate-400">—</span>}
      </Td>
      <Td>{r.salesman || <span className="text-slate-400 italic">—</span>}</Td>
      <Td className="text-center">
        <div className="flex items-center justify-center gap-1">
          <Button variant="outline" size="sm" onClick={preview}
                  data-testid={`drill-preview-${r.id}`}
                  className="h-6 px-2 py-0 text-[10px]">
            <Eye className="w-3 h-3" />
          </Button>
          <Button variant="outline" size="sm" onClick={sendEmail}
                  data-testid={`drill-send-${r.id}`}
                  className="h-6 px-2 py-0 text-[10px]">
            <Send className="w-3 h-3" />
          </Button>
          <Button variant="outline" size="sm" onClick={openEstimate}
                  data-testid={`drill-open-${r.id}`}
                  className="h-6 px-2 py-0 text-[10px]">
            <ExternalLink className="w-3 h-3" />
          </Button>
        </div>
      </Td>
    </tr>
  );
}
